<template>
  <v-container fluid class="pa-4">
    <v-card elevation="2" rounded="lg">
      <v-card-title class="d-flex align-center justify-space-between flex-wrap ga-3">
        <div>
          <div class="text-h5 font-weight-bold">
            Entregas en tienda
          </div>

          <div class="text-body-2 text-medium-emphasis mt-1">
            Listado de entregas en tienda pendientes de validar.
          </div>
        </div>

        <v-btn
          color="primary"
          variant="flat"
          :loading="loading"
          prepend-icon="mdi-refresh"
          @click="fetchData"
        >
          Actualizar
        </v-btn>
      </v-card-title>

      <v-divider />

      <v-card-text>
        <v-alert
          v-if="errorMessage"
          type="error"
          variant="tonal"
          class="mb-4"
        >
          {{ errorMessage }}
        </v-alert>

        <v-alert
          type="info"
          variant="tonal"
          class="mb-4"
        >
          Si el proveedor Traycco aparece en rojo, significa que no tiene ningún equipo asignado correctamente.
        </v-alert>

        <v-row class="mb-4">
          <v-col cols="12" md="4">
            <v-card variant="tonal" rounded="lg">
              <v-card-text>
                <div class="text-caption text-medium-emphasis">
                  Líneas
                </div>
                <div class="text-h5 font-weight-bold">
                  {{ items.length }}
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="4">
            <v-card variant="tonal" rounded="lg">
              <v-card-text>
                <div class="text-caption text-medium-emphasis">
                  Folletos
                </div>
                <div class="text-h5 font-weight-bold">
                  {{ totalFolletos.toLocaleString('es-ES') }}
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="4">
            <v-card
              variant="tonal"
              rounded="lg"
              :color="totalSinEquipo > 0 ? 'error' : undefined"
            >
              <v-card-text>
                <div class="text-caption text-medium-emphasis">
                  Sin equipo asignado
                </div>
                <div class="text-h5 font-weight-bold">
                  {{ totalSinEquipo }}
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-text-field
          v-model="search"
          label="Buscar por campaña, cliente, proveedor, equipo, zona o folleto"
          prepend-inner-icon="mdi-magnify"
          variant="outlined"
          density="compact"
          clearable
          hide-details
          class="mb-4"
        />

        <v-data-table
          :headers="headers"
          :items="items"
          :search="search"
          :loading="loading"
          item-value="id"
          density="compact"
          class="elevation-1 rounded-lg"
          :items-per-page="50"
        >
          <template #item.campaign_name="{ item }">
            <div class="font-weight-medium">
              {{ item.campaign_name }}
            </div>
          </template>

          <template #item.customer_name="{ item }">
            <div>
              {{ item.customer_name }}
            </div>
          </template>

          <template #item.provider_sort="{ item }">
            <div
              class="font-weight-medium"
              :class="item.needs_team_assignment ? 'text-error' : ''"
            >
              {{ item.partner_name || 'Sin proveedor' }}
            </div>

            <!--<v-chip
              v-if="item.is_traycco_provider"
              size="x-small"
              :color="item.needs_team_assignment ? 'error' : 'warning'"
              variant="tonal"
              class="mt-1"
            >
              Traycco
            </v-chip>-->
          </template>

          <template #item.team_sort="{ item }">
            <div
              :class="item.needs_team_assignment ? 'text-error font-weight-bold' : ''"
            >
              {{ item.team_name || 'Sin equipo' }}
            </div>

            <div
              v-if="item.needs_team_assignment"
              class="text-caption text-error"
            >
              Falta asignar equipo
            </div>
          </template>

          <template #item.zone_name="{ item }">
            <div class="text-right">
              {{ item.zone_name }}
            </div>
          </template>

          <template #item.zone_city="{ item }">
            <div>
              {{ item.zone_city }}
              <span v-if="item.zone_reference">
                , {{ item.zone_reference }}
              </span>
            </div>
          </template>

          <template #item.dates_sort="{ item }">
            <div class="text-caption">
              <div>Inicio: {{ formatDate(item.start_date) }}</div>
              <div>Fin: {{ formatDate(item.end_date) }}</div>

              <div
                v-if="item.distribute_date"
                class="text-success font-weight-medium"
              >
                Reparto: {{ formatDate(item.distribute_date) }}
              </div>
            </div>
          </template>

          <template #item.quantity="{ item }">
            <div class="text-right font-weight-medium">
              {{ Number(item.quantity || 0).toLocaleString('es-ES') }}
            </div>
          </template>

          <template #no-data>
            <div class="pa-6 text-center text-medium-emphasis">
              No hay entregas en tienda pendientes.
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import axios from 'axios'

const loading = ref(false)
const errorMessage = ref('')
const search = ref('')
const items = ref([])

const headers = [
  { title: 'Campaña', key: 'campaign_name', sortable: true },
  { title: 'Desc. campaña / Cliente', key: 'customer_name', sortable: true },
  { title: 'Proveedor', key: 'provider_sort', sortable: true },
  { title: 'Equipo', key: 'team_sort', sortable: true },
  { title: 'Zona Traycco', key: 'zone_name', sortable: true },
  { title: 'Población', key: 'zone_city', sortable: true },
  { title: 'Fechas', key: 'dates_sort', sortable: true },
  { title: 'Folletos', key: 'quantity', sortable: true, align: 'end' },
  { title: 'Ref. folleto', key: 'flyer_reference', sortable: true },
]

const totalFolletos = computed(() => {
  return items.value.reduce((total, item) => total + Number(item.quantity || 0), 0)
})

const totalSinEquipo = computed(() => {
  return items.value.filter(item => item.needs_team_assignment).length
})

async function fetchData() {
  loading.value = true
  errorMessage.value = ''

  try {
    const response = await axios.get('/api/admin/produccion/entregas-tienda')

    items.value = (response.data?.data || []).map(item => ({
      ...item,

      provider_sort: item.partner_name || '',
      team_sort: item.team_name || '',
      dates_sort: item.end_date || item.start_date || '',

      searchable_text: [
        item.campaign_name,
        item.customer_name,
        item.partner_name,
        item.team_name,
        item.zone_name,
        item.zone_city,
        item.zone_reference,
        item.flyer_reference,
      ].filter(Boolean).join(' '),
    }))
  } catch (error) {
    console.error(error)

    errorMessage.value =
      error.response?.data?.message ||
      'No se ha podido cargar el listado de entregas en tienda.'
  } finally {
    loading.value = false
  }
}

function formatDate(date) {
  if (!date) return '-'

  const parts = String(date).split('-')

  if (parts.length !== 3) {
    return date
  }

  return `${parts[2]}/${parts[1]}/${parts[0]}`
}

onMounted(() => {
  fetchData()
})
</script>